/**
 * NewAdminDrawer — Pacote 8: Promover user existente a admin em 1 chamada.
 *
 * Fluxo:
 *   1. Insere email do user já cadastrado
 *   2. Marca os switches por área (4 áreas × 4 níveis = 16)
 *   3. Submit: POST /api/v2/admin/permissions/promote
 *
 * Tratamento de erros especiais:
 *   - 404 NOT_FOUND → alerta com instrução de cadastrar em "Produtores/Afiliados"
 *   - 422 BUSINESS_RULE → alerta vermelho persistente (limite 3 Admin Masters)
 *   - Demais → toast
 */

import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import adminPermissionsV2API from '../../services/api/v2/adminPermissions';
import ModernSwitch from '../../components/forms/ModernSwitch';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AREA_LABELS = {
  general: 'Geral (Técnico)',
  tickets: 'Suporte',
  financial: 'Financeiro',
  administrative: 'Administrativo',
};
const AREA_DESCRIPTIONS = {
  general: 'Acesso pleno ao sistema. Admin Master = 4 níveis ligados aqui.',
  tickets: 'Helpdesk: ver/responder tickets, alterar status.',
  financial: 'Saques: aprovar/rejeitar, visualizar saldos.',
  administrative: 'Operacional: gestão de usuários e cadastros.',
};
const AREAS = ['general', 'tickets', 'financial', 'administrative'];
const LEVELS = ['view', 'edit', 'approve', 'delete'];
const LEVEL_LABELS = {
  view: 'Visualizar',
  edit: 'Editar',
  approve: 'Aprovar',
  delete: 'Deletar',
};

function emptyPermissionsMap() {
  const map = {};
  AREAS.forEach((a) => {
    map[a] = { can_view: false, can_edit: false, can_approve: false, can_delete: false };
  });
  return map;
}

const NewAdminDrawer = ({ isOpen, onClose, onCreated }) => {
  const [email, setEmail] = useState('');
  const [perms, setPerms] = useState(emptyPermissionsMap());
  const [errors, setErrors] = useState({});
  const [topError, setTopError] = useState('');
  const [persistentError, setPersistentError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setEmail('');
    setPerms(emptyPermissionsMap());
    setErrors({});
    setTopError('');
    setPersistentError('');
  }, [isOpen]);

  const validate = () => {
    const err = {};
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) err.email = 'Email obrigatório';
    else if (!EMAIL_REGEX.test(trimmed)) err.email = 'Email inválido';

    // Pelo menos 1 nível selecionado em alguma área
    const hasAnyPerm = AREAS.some((a) =>
      perms[a].can_view || perms[a].can_edit || perms[a].can_approve || perms[a].can_delete,
    );
    if (!hasAnyPerm) err.permissions = 'Marque ao menos 1 permissão';

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  // Cascata: ligar nível superior liga view; desligar view zera os superiores
  const toggleLevel = (area, level, value) => {
    setPerms((prev) => {
      const current = { ...prev[area] };
      current[`can_${level}`] = value;
      if (value && level !== 'view') current.can_view = true;
      if (!value && level === 'view') {
        current.can_edit = false;
        current.can_approve = false;
        current.can_delete = false;
      }
      return { ...prev, [area]: current };
    });
    setErrors((p) => ({ ...p, permissions: undefined }));
  };

  const handleSubmit = async () => {
    if (!validate() || submitting) return;
    try {
      setSubmitting(true);
      setTopError('');
      setPersistentError('');
      const payload = {
        email: email.trim().toLowerCase(),
        permissions: AREAS.map((a) => ({ area: a, ...perms[a] })),
      };
      await adminPermissionsV2API.promote(payload);
      toast.success(`${email} virou admin e recebeu as permissões.`);
      onCreated?.();
      onClose?.();
    } catch (err) {
      const status = err.response?.status;
      const code = err.response?.data?.code;
      const message = err.response?.data?.message
        || err.message
        || 'Erro ao promover admin';

      if (status === 404 || code === 'NOT_FOUND') {
        setPersistentError(
          'Nenhum usuário encontrado com este email. Cadastre o usuário primeiro em "Usuários > Produtores/Afiliados" e tente novamente.',
        );
      } else if (status === 422 || code === 'BUSINESS_RULE') {
        setPersistentError(message);
      } else {
        setTopError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose?.();
      }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1050,
        display: 'flex', justifyContent: 'flex-end',
      }}
    >
      <div
        style={{
          width: 'min(560px, 96%)', maxHeight: '100vh', background: '#fff',
          display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,.15)',
        }}
      >
        {/* Header */}
        <div style={{ padding: 20, borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <Icon icon="mdi:account-plus" width="24" />
              <div>
                <span style={{ display: 'block', fontSize: 16, fontWeight: 700 }}>Novo Admin</span>
                <span style={{ display: 'block', fontSize: 13, color: '#6b7280' }}>
                  Promover usuário existente
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{ background: 'transparent', border: 0, cursor: 'pointer' }}
              aria-label="Fechar"
            >
              <Icon icon="mdi:close" width="22" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {persistentError && (
            <div className="alert alert-danger" role="alert" style={{ marginBottom: 16 }}>
              <Icon icon="mdi:alert-circle" width="18" style={{ marginRight: 6 }} />
              {persistentError}
            </div>
          )}
          {topError && !persistentError && (
            <div className="alert alert-warning" role="alert" style={{ marginBottom: 16 }}>
              {topError}
            </div>
          )}

          <div style={{ marginBottom: 20 }}>
            <label className="form-label fw-semibold">
              Email do usuário <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="email"
              className="form-control"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setErrors((p) => ({ ...p, email: undefined }));
              }}
              placeholder="email@exemplo.com"
              disabled={submitting}
              autoComplete="off"
            />
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              O usuário precisa já estar cadastrado na plataforma.
            </span>
            {errors.email && (
              <div style={{ color: '#dc2626', fontSize: 13, marginTop: 4 }}>
                <Icon icon="mdi:alert-circle-outline" width="14" /> {errors.email}
              </div>
            )}
          </div>

          {/* Permissões por área */}
          <span style={{ display: 'block', fontWeight: 600, marginBottom: 10 }}>Permissões</span>
          {errors.permissions && (
            <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 10 }}>
              <Icon icon="mdi:alert-circle-outline" width="14" /> {errors.permissions}
            </div>
          )}
          {AREAS.map((area) => (
            <div
              key={area}
              style={{
                border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, marginBottom: 12,
              }}
            >
              <div style={{ marginBottom: 8 }}>
                <span style={{ display: 'block', fontWeight: 700, fontSize: 14 }}>
                  {AREA_LABELS[area]}
                </span>
                <span style={{ display: 'block', fontSize: 12, color: '#6b7280' }}>
                  {AREA_DESCRIPTIONS[area]}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                {LEVELS.map((level) => (
                  <div key={level} style={{ textAlign: 'center' }}>
                    <span style={{ display: 'block', fontSize: 11, color: '#374151', marginBottom: 4 }}>
                      {LEVEL_LABELS[level]}
                    </span>
                    <ModernSwitch
                      checked={perms[area][`can_${level}`]}
                      onChange={(v) => toggleLevel(area, level, v)}
                      disabled={submitting}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 10 }}>
            <Icon icon="mdi:shield-crown" width="14" style={{ marginRight: 4 }} />
            Admin Master = todos os 4 níveis ligados na área <b>Geral</b>. Limite hard de 3 contas.
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: 16, borderTop: '1px solid #e5e7eb',
            display: 'flex', gap: 10, justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Icon icon="mdi:loading" className="spin" width="14" style={{ marginRight: 6 }} />
                Promovendo…
              </>
            ) : (
              <>
                <Icon icon="mdi:check" width="14" style={{ marginRight: 6 }} />
                Promover a Admin
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewAdminDrawer;
